import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SEOHelmet } from 'components/SEO';
import LoadingIndicator from 'components/LoadingIndicator';
import { useGetFollowingFeedQuery } from 'store/services/feed/followingFeedApi';
import { useGetMyFollowingQuery } from 'store/services/social/followApi';
import { FeedTypeFilter } from 'types/interfaces/followingFeed';
import { FeedEventCard } from './components/FeedEventCard';
import {
  NewsfeedFilterTabs,
  FilterTabKey,
} from './components/NewsfeedFilterTabs';
import './NewsfeedPage.css';

const toQueryType = (filter: FilterTabKey): FeedTypeFilter | undefined =>
  filter === 'ALL' ? undefined : filter;

const NewsfeedPage: React.FC = () => {
  const [filter, setFilter] = useState<FilterTabKey>('ALL');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const queryArgs = useMemo(
    () => ({ type: toQueryType(filter), cursor }),
    [filter, cursor],
  );

  const { data, isLoading, isFetching } = useGetFollowingFeedQuery(queryArgs);
  const { data: followingData, isLoading: isLoadingFollowing } =
    useGetMyFollowingQuery();

  const handleFilterChange = useCallback((next: FilterTabKey) => {
    setFilter(next);
    setCursor(undefined);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!data?.hasMore || !data.nextCursor || isFetching) return;
    setCursor(data.nextCursor);
  }, [data?.hasMore, data?.nextCursor, isFetching]);

  // Infinite scroll bound to .main-page-content — matches the pattern used by
  // the Community Feed page so the feed list does not introduce its own nested
  // scrollbar on top of the main content scroll.
  const loadMoreRef = useRef(handleLoadMore);
  useEffect(() => {
    loadMoreRef.current = handleLoadMore;
  }, [handleLoadMore]);

  useEffect(() => {
    const el = document.querySelector('.main-page-content');
    if (!el) return;
    const handler = () => {
      const scrollTop = (el as HTMLElement).scrollTop;
      const clientHeight = (el as HTMLElement).clientHeight;
      const scrollHeight = (el as HTMLElement).scrollHeight;
      if (scrollHeight - (scrollTop + clientHeight) < 400) {
        loadMoreRef.current();
      }
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, []);

  const hasNoFollows =
    !isLoadingFollowing && (followingData?.users?.length ?? 0) === 0;

  const events = data?.events ?? [];
  const isInitialLoading = isLoading && !data;

  return (
    <>
      <SEOHelmet
        title="Recent Activity - TradeMagic"
        description="Recent activity from traders you follow."
        canonical="https://trademagic.gg/newsfeed"
      />

      <div className="newsfeed-page">
        <header className="newsfeed-page__header">
          <h1 className="newsfeed-page__title">Recent Activity</h1>
          <p className="newsfeed-page__subtitle">
            New wanted cards, completed trades, and milestones from traders
            you follow.
          </p>
        </header>

        <NewsfeedFilterTabs active={filter} onChange={handleFilterChange} />

        {hasNoFollows ? (
          <div className="newsfeed-empty">
            <p>
              You&apos;re not following anyone yet. Complete a trade to
              automatically follow your trading partners, or visit a
              trader&apos;s profile to follow them manually.
            </p>
          </div>
        ) : isInitialLoading ? (
          <div className="newsfeed-loading">
            <LoadingIndicator />
          </div>
        ) : events.length === 0 ? (
          <div className="newsfeed-empty">
            <p>
              No activity yet for this filter. Check back soon — your
              followed traders&apos; latest moves will show up here.
            </p>
          </div>
        ) : (
          <div className="newsfeed-list">
            {events.map((event) => (
              <FeedEventCard key={event.id} event={event} />
            ))}

            {isFetching && data && (
              <div className="newsfeed-loading-more">
                <LoadingIndicator />
              </div>
            )}

            {!data?.hasMore && events.length > 0 && (
              <div className="newsfeed-end-of-feed">
                You&apos;re all caught up.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default NewsfeedPage;
